import { log } from '@/log'
import { HlAppMode } from '@/models/hl-app/hl-app-mode'
import { SessionApiService } from '@/services/api/session'
import { SessionApiErrorResponse } from '@/services/api/session/response'
import { SessionCookie, SessionCookiePayload } from '@/services/session/session-cookie'

const namespace = 'initialize-session'

/**
 * 正しいセッションがcookieに存在する状態に遷移する初期化処理
 * - cookieが存在する
 * - cookieの情報がSSOT（技術的にはRedisのレコード）と等しい
 *
 * ただし、結果を保持しているため、厳密には「1度SSOTと等しい状態に移行した」ことのみを保証している点に注意
 * 初期化処理完了後にcookieが書き換えられた場合に、初期化処理は再実行されない
 */
export class InitializeSession {
  static exec(): Promise<void> {
    return InitializeSession.getInstance().exec()
  }

  static onInitialized<T>(callback: () => Promise<T>): Promise<T> {
    return InitializeSession.getInstance().onInitialized(callback)
  }

  static getInstance(): InitializeSession {
    if (!InitializeSession.instance) {
      InitializeSession.instance = new InitializeSession()
    }

    return InitializeSession.instance
  }

  private static instance: InitializeSession

  private initializePromise: Promise<void> | null = null

  private constructor(
    private readonly sessionApiService: SessionApiService = new SessionApiService(),
    private readonly sessionCookie: SessionCookie = new SessionCookie()
  ) {}

  exec(): Promise<void> {
    if (this.initializePromise) {
      return this.initializePromise
    }

    this.initializePromise = this.innerInitialize()
      .then((payload) => {
        log.debug({ service: `${namespace}/initialize`, payload })

        return Promise.resolve()
      })

    return this.initializePromise
  }

  onInitialized<T>(callback: () => Promise<T>): Promise<T> {
    return this.exec().then(callback)
  }

  /**
   * - セッションcookieが存在する（client側でパースできる範囲で正しいと思われる）
   *   - keepCurrentする
   *   - keepCurrentが正常に完了すれば成功
   *   - keepCurrentがセッション不正のエラーを返した場合、セッションが存在しないルートを実行する
   *   - keepCurrentがそれ以外のエラーを返した場合、Promiseをrejectする
   * - セッションcookieが存在しない
   *   - newする
   *   - newが正常に完了すれば成功
   *   - newが正常に完了しなければPromiseをrejectする
   */
  private async innerInitialize(): Promise<SessionCookiePayload> {
    const maybePayload = this.sessionCookie.readPayload()
    if (maybePayload) {
      const keepResult = await this.sessionApiService.keepCurrent()

      return keepResult.fold(
        (error: SessionApiErrorResponse) => {
          if (error.errorCode === 'invalid_cookie' || error.errorCode === 'not_found') {
            return this.new()
          }

          throw new Error(`${error.errorCode}: ${error.message}`)
        },
        () => {
          // keepCurrentに成功したのでcookieは存在するはず
          // 存在しなければrejectする
          return Promise.resolve(this.sessionCookie.readPayloadOrFail())
        }
      )
    }

    return this.new()
  }

  private async new(): Promise<SessionCookiePayload> {
    const isHlApp = HlAppMode.isPageForHlApp || HlAppMode.isViewedInHlAppWebView
    const result = await this.sessionApiService.new({
      isHlApp,
    })

    return result.fold(
      (error: SessionApiErrorResponse) => {
        throw new Error(`${error.errorCode}: ${error.message}`)
      },
      // newに成功したのでcookieは存在するはず
      // 存在しなければrejectする
      () => Promise.resolve(this.sessionCookie.readPayloadOrFail())
    )
  }
}
